import React, { useCallback, useMemo, useState } from 'react'

import { cloneDeep } from 'lodash'

import { useReactFlow } from '@xyflow/react'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'

import Node, { InputType } from 'components_new/molecules/Node'
import Caption from 'components_new/molecules/Node/Caption'
import ExpressionField from 'components_new/molecules/Node/ExpressionField'
import { FieldOption } from 'components_new/molecules/Node/FieldSelect'
import { BOARD } from 'components_new/molecules/Node/accents'

import NodeType from 'types/transformation/enums/NodeType'
import DataType from 'types/transformation/enums/DataType'
import { CalculateNode, Node as TNode } from 'types/transformation/types/nodes'

import {
  useInputSchema,
  useIsInvalid,
  usePruneRefs,
  useColumnOrigins
} from '../hooks'

import { getExpressionRefs } from './utils'

interface CalculateNodeProps {
  id: string
  data: CalculateNode
  selected: boolean
}

const Calculate = (props: CalculateNodeProps) => {
  const { id, data, selected } = props

  const { setNodes } = useReactFlow<TNode>()

  const isInvalid = useIsInvalid(data)
  const inputSchema = useInputSchema(data.left_node)

  const [nameFocused, setNameFocused] = useState(false)

  const numbersSchema = useMemo(
    () =>
      inputSchema.filter((column) =>
        'type' in column
          ? [DataType.BIGINT, DataType.DOUBLE].includes(column.type)
          : false
      ),
    [inputSchema]
  )

  // Which source table each Input node reads, so an attribute can show its
  // origin table name in the autocomplete (not just the node number).
  const originByNode = useColumnOrigins()

  const fieldOptions = useMemo(
    (): FieldOption[] =>
      numbersSchema.map((column) => ({
        id: column.id,
        name: column.name,
        type: column.type,
        nodeIndex: column.reference_node_index,
        origin: originByNode.get(column.reference_node) ?? null
      })),
    [numbersSchema, originByNode]
  )

  // ✓ valid when every reference resolves to an upstream column, ⚠ otherwise.
  // A `@` in the stored value is an unresolved fragment (the field maps
  // resolved refs to bare ids), invalid no matter what else resolved.
  const status = useMemo(() => {
    if (data.expression.includes('@')) {
      return 'invalid'
    }

    const referenced = getExpressionRefs(data.expression)

    if (referenced.length === 0) {
      return null
    }

    const validIds = new Set(numbersSchema.map((column) => column.id))

    return referenced.every((columnId) => validIds.has(columnId))
      ? 'valid'
      : 'invalid'
  }, [data.expression, numbersSchema])

  // input schema has changed, validate references
  usePruneRefs<CalculateNode>(
    id,
    (nodeData) => {
      // An empty schema is an unresolved one (input not connected yet, or
      // sources still loading); wiping against it would clear expressions
      // whose columns are fine.
      if (numbersSchema.length === 0) {
        return nodeData
      }

      const columnIds = getExpressionRefs(nodeData.expression)

      const validIds = new Set(numbersSchema.map((o) => o.id))
      const missingColumns = columnIds.some((v) => !validIds.has(v))

      if (missingColumns) {
        // column doesnt exists anymore, clear expression
        return {
          ...nodeData,
          expression: ''
        }
      }

      return nodeData
    },
    [numbersSchema]
  )

  const handleChangeName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const name = event.target.value

      setNodes((existingNodes) =>
        existingNodes.map((node) => {
          if (node.id !== id) {
            return node
          }

          const newNodeData = cloneDeep(node.data) as CalculateNode

          newNodeData.new_column.name = name

          return {
            ...node,
            data: newNodeData
          }
        })
      )
    },
    [setNodes, id]
  )

  const handleChangeExpression = useCallback(
    (newExpression: string) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) => {
          if (node.id !== id) {
            return node
          }

          return {
            ...node,
            data: {
              ...node.data,
              expression: newExpression
            }
          }
        })
      )
    },
    [setNodes, id]
  )

  return (
    <Node
      error={isInvalid}
      icon="TransformOutlined"
      inputType={InputType.SINGLE}
      title={'Calculate'}
      selected={selected}
      nodeNumber={data.index}
      type={NodeType.CALCULATE}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          width: '300px'
        }}
      >
        <Caption>Namn på nytt attribut</Caption>
        <input
          className="nodrag ignore-locked-mode"
          onBlur={() => setNameFocused(false)}
          onChange={handleChangeName}
          onFocus={() => setNameFocused(true)}
          placeholder="t.ex. tusen"
          value={data.new_column.name}
          style={{
            background: nameFocused ? '#ffffff' : BOARD.rowBackground,
            border: `1px solid ${
              nameFocused ? BOARD.signalGreen : 'transparent'
            }`,
            borderRadius: '10px',
            boxSizing: 'border-box',
            color: BOARD.textPrimary,
            fontFamily: BOARD.fontBody,
            fontSize: '13.5px',
            fontWeight: 500,
            outline: 'none',
            padding: '9px 11px',
            width: '100%'
          }}
        />

        <Box sx={{ mt: 1 }}>
          <Caption>Uttryck</Caption>
        </Box>
        <ExpressionField
          value={data.expression}
          options={fieldOptions}
          onChange={handleChangeExpression}
        />

        {status ? (
          <Box
            sx={{
              alignItems: 'center',
              color: status === 'valid' ? BOARD.signalGreenDark : '#B91C1C',
              display: 'flex',
              fontFamily: BOARD.fontBody,
              fontSize: '11.5px',
              gap: 0.75,
              p: '2px'
            }}
          >
            <Icon
              name={status === 'valid' ? 'Check' : 'WarningAmberOutlined'}
              sx={{ fontSize: '14px' }}
            />
            {status === 'valid'
              ? 'Giltigt uttryck'
              : 'Okänt attribut i uttrycket'}
          </Box>
        ) : null}
      </Box>
    </Node>
  )
}

export default Calculate
